import React, { useState, useCallback, useMemo, useEffect } from "react";
import { Plus } from "lucide-react";
import { useCompaniesPaginated, useCompany } from "@/lib/hooks/api/companies";
import { useCreateCompany } from "@/features/companies/hooks/companies-mutations";
import type { SelectOption } from "@/components/ui/responsive-select";
import { EntitySelectShell } from "./shared/EntitySelectShell";
import { useAccumulatedList } from "./shared/useAccumulatedList";
import { useFTSWithDebouncedSearch } from "./shared/useFTSWithDebouncedSearch";
import { renderCompanyOption } from "./CompanyOptionPreview";

/** Sentinel option value for the "Add new company named X" inline-create row. */
const ADD_NEW_COMPANY_VALUE = "__add_new_company__";

interface CompanySelectProps {
  value: string;
  // Permissive option type preserved from the original public signature so
  // existing callers (table inline edit, filter panel, etc.) keep type-checking.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onValueChange: (value: string, option?: any) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  items?: any[];
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  required?: boolean;
  isSearching?: boolean;
  onSearchChange?: (query: string) => void;
  searchTerm?: string;
  fullWidth?: boolean;
  centerOnScreen?: boolean;
  expandInline?: boolean;
  expandInlineVariant?: "replace" | "floating";
  highZIndex?: boolean;
  onOpenChange?: (open: boolean) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

interface CompanyItem {
  id: string;
  name: string;
  company_name?: string;
  industry?: string;
  website?: string;
  city?: string;
  state?: string;
}

export const CompanySelectWithModal = React.memo<CompanySelectProps>((props) => {
  const {
    value,
    onValueChange,
    items,
    placeholder = "Search companies...",
    disabled,
    isSearching,
    onSearchChange,
    centerOnScreen,
    expandInline = true,
    expandInlineVariant = "floating",
    onOpenChange,
    highZIndex,
  } = props;

  // FTS-driven debounced search (minSearchLen: 1 matches prior `> 1` behavior).
  const {
    searchValue,
    setSearchValue,
    hasSearchTerm,
    ftsResults,
    ftsLoading,
  } = useFTSWithDebouncedSearch<CompanyItem>({
    entityType: "companies",
    ftsLimit: 50,
  });

  // Pagination for browse mode (no search). Zero-based pages.
  const [page, setPage] = useState(0);
  const limit = 30;
  const {
    data: companiesData,
    isLoading: paginationFetching,
    isFetching: isBackgroundFetching,
  } = useCompaniesPaginated({
    page,
    limit,
    search: hasSearchTerm ? undefined : undefined,
    orderBy: "name",
    orderDirection: "asc",
  });

  useEffect(() => {
    if (!hasSearchTerm) setPage(0);
  }, [hasSearchTerm]);

  // Browse-mode accumulator. Paused while FTS search is active.
  const currentPageItems = useMemo<CompanyItem[]>(
    () => (companiesData?.data ?? []) as CompanyItem[],
    [companiesData]
  );
  const accumulatedCompanies = useAccumulatedList<CompanyItem>({
    page,
    firstPage: 0,
    items: currentPageItems,
    idOf: (c) => String(c.id),
    paused: hasSearchTerm,
  });

  // FTS results in search mode; accumulated list otherwise.
  const baseSourceCompanies = useMemo<CompanyItem[]>(() => {
    if (hasSearchTerm) return ftsResults ?? [];
    return accumulatedCompanies.length > 0
      ? accumulatedCompanies
      : currentPageItems;
  }, [hasSearchTerm, ftsResults, accumulatedCompanies, currentPageItems]);

  // Preselected value not present in browse/FTS lists (e.g. opened pre-filled
  // from another entity row) — fetch it by id so its label renders instead of
  // the placeholder. Without this, value=<uuid> with no matching option shows
  // empty in the trigger even though the form holds the id correctly.
  const needsPreselectLookup =
    !!value && !baseSourceCompanies.some((c) => String(c.id) === value);
  const { data: preselectedCompany } = useCompany(
    needsPreselectLookup ? value : null,
    { enabled: needsPreselectLookup }
  );

  const sourceCompanies = useMemo<CompanyItem[]>(() => {
    if (
      preselectedCompany &&
      typeof preselectedCompany === "object" &&
      "id" in preselectedCompany &&
      "name" in preselectedCompany &&
      !baseSourceCompanies.some((c) => String(c.id) === String(preselectedCompany.id))
    ) {
      return [preselectedCompany as CompanyItem, ...baseSourceCompanies];
    }
    return baseSourceCompanies;
  }, [baseSourceCompanies, preselectedCompany]);

  const buildOptions = useCallback(
    (companies: CompanyItem[] | undefined): SelectOption[] => {
      const source: CompanyItem[] =
        sourceCompanies.length > 0 ? sourceCompanies : companies || [];

      return source
        .filter((c) => c?.id && c?.name)
        .map((c) => {
          const label = c.name || `Company ${c.id}`;
          const location = [c.city, c.state].filter(Boolean).join(", ");
          return {
            value: String(c.id),
            label,
            searchableText: [c.name, c.industry, c.website, c.city, c.state]
              .filter(Boolean)
              .join(" "),
            industry: c.industry,
            website: c.website,
            location: location || undefined,
          } as SelectOption;
        });
    },
    [sourceCompanies]
  );

  const baseOptions = useMemo(
    () => buildOptions(items as CompanyItem[] | undefined),
    [buildOptions, items]
  );

  // Inline create: when the user has typed a name that doesn't already match an
  // existing option, offer an "Add new company named X" row at the top.
  const createCompany = useCreateCompany();
  const trimmedSearch = searchValue.trim();
  const showAddNew = useMemo(() => {
    if (trimmedSearch.length < 2 || createCompany.isPending) return false;
    const lowered = trimmedSearch.toLowerCase();
    return !baseOptions.some((o) => o.label.trim().toLowerCase() === lowered);
  }, [trimmedSearch, baseOptions, createCompany.isPending]);

  const options = useMemo<SelectOption[]>(() => {
    if (!showAddNew) return baseOptions;
    const addNewOption: SelectOption = {
      value: ADD_NEW_COMPANY_VALUE,
      label: `Add new company "${trimmedSearch}"`,
      searchableText: trimmedSearch,
    };
    return [addNewOption, ...baseOptions];
  }, [showAddNew, baseOptions, trimmedSearch]);

  const handleAddNewCompany = useCallback(async () => {
    const name = trimmedSearch;
    if (!name) return;
    try {
      const created = await createCompany.mutateAsync({ name });
      if (created?.id) {
        onValueChange(String(created.id), {
          value: String(created.id),
          label: created.name ?? name,
        });
        setSearchValue("");
      }
    } catch {
      // useCreateCompany already surfaces a toast on error.
    }
  }, [trimmedSearch, createCompany, onValueChange, setSearchValue]);

  // Render the sentinel row distinctly; delegate real companies to the shared renderer.
  const renderOption = useCallback((option: SelectOption): React.ReactNode => {
    if (option.value === ADD_NEW_COMPANY_VALUE) {
      return (
        <div className="flex items-center gap-2 font-medium text-primary">
          <Plus className="h-4 w-4 flex-shrink-0" />
          <span className="truncate">{option.label}</span>
        </div>
      );
    }
    return renderCompanyOption(option);
  }, []);

  const isFetching = hasSearchTerm ? ftsLoading : paginationFetching;
  const isLoading =
    (typeof isSearching === "boolean" ? isSearching : undefined) ?? isFetching;

  const totalCount = companiesData?.count ?? 0;
  const totalPages = Math.ceil(totalCount / limit);
  const handleLoadMore = useCallback(() => {
    setPage((p) => p + 1);
  }, []);

  return (
    <EntitySelectShell
      value={value}
      onValueChange={onValueChange}
      options={options}
      placeholder={placeholder}
      disabled={disabled}
      searchValue={searchValue}
      setSearchValue={setSearchValue}
      onSearchChange={onSearchChange}
      isLoading={!!isLoading}
      recentKey="companies"
      highZIndex={highZIndex}
      searchPlaceholder="Search companies..."
      title="Select Company"
      description="Choose a company from the list"
      centerOnScreen={centerOnScreen !== false}
      expandInline={expandInline}
      expandInlineVariant={expandInlineVariant}
      onOpenChange={onOpenChange}
      customOptionRenderer={renderOption}
      addNewSentinel={ADD_NEW_COMPANY_VALUE}
      onAddNew={handleAddNewCompany}
      pageInfo={{
        page,
        totalPages,
        firstPage: 0,
        isBackgroundFetching: !!isBackgroundFetching,
        onLoadMore: handleLoadMore,
      }}
      disablePagination={hasSearchTerm}
      containerClassName="relative w-full"
      className="w-full"
    />
  );
});

CompanySelectWithModal.displayName = "CompanySelectWithModal";
